import { Dialog } from 'primereact/dialog';
import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Fetch } from '../../shared/Fetch';
import { QAInput } from '../../shared/QAInput';

interface IProps {
    singleData: any;
    open: boolean;
    hideDialog: () => void;
    toast: any;
    reload: boolean;
    setReload: (is: boolean) => void;
    hasPermission: (pageName: string, featureName?: string) => Boolean;
}

export interface IFormData {
    id?: number;
    projectID?: number;
    projectName?: string;
    module: string;
    subModule: string;
    scenario: string;
    description: string;
    testType: boolean;
    stepsToExecute: string;
    preconditions: string;
    expectedResult: string;
    actualResult: string;
    status: boolean;
    comment?: string;
}

export const emptyFormData: IFormData = {
    module: '',
    subModule: '',
    scenario: '',
    description: '',
    testType: false,
    stepsToExecute: '',
    preconditions: '',
    expectedResult: '',
    actualResult: '',
    status: false,
};

const schema = yup.object().shape({
    module: yup.string().required('Module required'),
    subModule: yup.string().required('Sub-Module required'),
    scenario: yup.string().required('Scenario required'),
    description: yup.string().required('Description required'),
    stepsToExecute: yup.string().required('Steps To Execute required'),
    preconditions: yup.string().required('Preconditions required'),
    expectedResult: yup.string().required('Expected Result required'),
    actualResult: yup.string().required('Actual Result required'),
    status: yup.boolean().required('Status required'),
    testType: yup.boolean().required('Test Type required'),
});

export const Form = (props: IProps) => {
    const [loading, setLoading] = useState(false);
    const [steps, setSteps] = useState<string[]>(['']);
    const [preconditionsList, setPreconditionsList] = useState<string[]>(['']);

    const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<IFormData>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData
    });

    useEffect(() => {
        setValue("stepsToExecute", steps.filter(s => s.trim() !== "").join(", "), { shouldValidate: true });
    }, [steps, setValue]);

    useEffect(() => {
        setValue("preconditions", preconditionsList.filter(s => s.trim() !== "").join(", "), { shouldValidate: true });
    }, [preconditionsList, setValue]);

    useEffect(() => {
        setSteps(props.singleData?.stepsToExecute ? props.singleData.stepsToExecute.split(',').map((s: string) => s.trim()) : ['']);
        setPreconditionsList(props.singleData?.preconditions ? props.singleData.preconditions.split(',').map((p: string) => p.trim()) : ['']);
        reset(props.singleData);
    }, [props.singleData]);

    const handleStepChange = (index: number, value: string) => {
        const updated = [...steps];
        updated[index] = value;
        setSteps(updated);
    };

    const handlePreconditionChange = (index: number, value: string) => {
        const updated = [...preconditionsList];
        updated[index] = value;
        setPreconditionsList(updated);
    };

    const addStep = () => setSteps(prev => [...prev, '']);
    const removeStep = (index: number) => setSteps(prev => prev.filter((_, i) => i !== index));
    const addPrecondition = () => setPreconditionsList(prev => [...prev, '']);
    const removePrecondition = (index: number) => setPreconditionsList(prev => prev.filter((_, i) => i !== index));

    const onSubmit = async (data: IFormData) => {
        setLoading(true);
        data.stepsToExecute = steps.filter(s => s.trim() !== '').join(', ');
        data.preconditions = preconditionsList.filter(s => s.trim() !== '').join(', ');
        const method = props.singleData?.id ? "PUT" : "POST";
        const message = props.singleData?.id ? " updated successfully." : "Posted successfully.";
        const response = await Fetch("TestCases", method, data, null, props.toast);
        if (response) {
            props.setReload(true);
            props.hideDialog();
            props.toast.current.show({ severity: 'success', detail: message, life: 3000 });
        }
        setLoading(false);
    };

    const onHide = () => {
        props.hideDialog();
        reset(emptyFormData);
        setSteps(['']);
        setPreconditionsList(['']);
    };

    return (
        <Dialog visible={props.open} style={{ width: '70vw' }} header="Add/Edit Test Cases" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid ">

                <QAInput required name='module' control={control} label='Module' column={9} error={errors.module?.message} />
                <QAInput required name='subModule' column={9} control={control} label='Sub Module' error={errors.subModule?.message} />
                <QAInput required name='scenario' column={9} control={control} label='Scenario' error={errors.scenario?.message} />
                <QAInput required name='description' column={9} control={control} label='Description' error={errors.description?.message} />

                {/* Test Type */}
                <div className="mb-3">
                    <label className="font-lg mb-2">Test Type <span style={{ color: 'red' }}>*</span></label>
                    <div className="flex gap-4 mt-2">
                        <div className="flex align-items-center">
                            <input type="radio" checked={watch('testType') === true} onChange={() => setValue('testType', true, { shouldValidate: true })} />
                            <label className="ml-2">Positive</label>
                        </div>
                        <div className="flex align-items-center">
                            <input type="radio" checked={watch('testType') === false} onChange={() => setValue('testType', false, { shouldValidate: true })} />
                            <label className="ml-2">Negative</label>
                        </div>
                    </div>
                    {errors.testType && <small className="p-error">{errors.testType.message}</small>}
                </div>

                {/* Steps */}
                <div className="mb-3">
                    <label className="mb-2">Steps To Execute <span className="text-red-500">*</span></label>
                    {steps.map((step, index) => (
                        <div key={index} className="flex items-center gap-2 mb-2">
                            <input type="text" value={step} onChange={(e) => handleStepChange(index, e.target.value)} className="p-inputtext p-component flex-1" placeholder={`Step ${index + 1}`} />
                            {steps.length > 1 && (
                                <Button type="button" icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-sm" onClick={() => removeStep(index)} />
                            )}
                            {index === steps.length - 1 && (
                                <Button type="button" icon="pi pi-plus" className="p-button-rounded p-button-success p-button-sm" onClick={addStep} />
                            )}
                        </div>
                    ))}
                    {errors.stepsToExecute && <small className="p-error">{errors.stepsToExecute.message}</small>}
                </div>

                {/* Preconditions */}
                <div className="mb-3">
                    <label className="mb-2">Preconditions <span className="text-red-500">*</span></label>
                    {preconditionsList.map((pre, index) => (
                        <div key={index} className="flex items-center gap-2 mb-2">
                            <input type="text" value={pre} onChange={(e) => handlePreconditionChange(index, e.target.value)} className="p-inputtext p-component flex-1" placeholder={`Precondition ${index + 1}`} />
                            {preconditionsList.length > 1 && (
                                <Button type="button" icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-sm" onClick={() => removePrecondition(index)} />
                            )}
                            {index === preconditionsList.length - 1 && (
                                <Button type="button" icon="pi pi-plus" className="p-button-rounded p-button-success p-button-sm" onClick={addPrecondition} />
                            )}
                        </div>
                    ))}
                    {errors.preconditions && <small className="p-error">{errors.preconditions.message}</small>}
                </div>

                <QAInput required name='expectedResult' column={9} control={control} label='Expected Result' error={errors.expectedResult?.message} />
                <QAInput required name='actualResult' column={9} control={control} label='Actual Result' error={errors.actualResult?.message} />

                {/* Status */}
                <div className="mb-3">
                    <label className="font-lg mb-2">Status <span style={{ color: 'red' }}>*</span></label>
                    <div className="flex gap-4 mt-2">
                        <div className="flex align-items-center">
                            <input type="radio" checked={watch('status') === true} onChange={() => setValue('status', true, { shouldValidate: true })} />
                            <label className="ml-2">Pass</label>
                        </div>
                        <div className="flex align-items-center">
                            <input type="radio" checked={watch('status') === false} onChange={() => setValue('status', false, { shouldValidate: true })} />
                            <label className="ml-2">Fail</label>
                        </div>
                    </div>
                    {errors.status && <small className="p-error">{errors.status.message}</small>}
                </div>

                <QAInput name='comment' column={9}  control={control} label='Comment' error={errors.comment?.message} />

                {/* Buttons */}
                <div className="flex gap-2 justify-end">
                    <Button type="submit" label="Save" loading={loading} size="small" className="p-button-success" />
                    <Button type="button" label="Cancel" size="small" className="p-button-secondary" onClick={() => reset()} />
                </div>
            </form>
        </Dialog>
    );
};
